import { COLORS } from '../../IndividualPlan/tokens';
import { DateInferredDepartureNote } from '../components/DateInferredDepartureNote';
import { DerivedDepartureNote } from '../components/DerivedDepartureNote';
import { type Finding } from '../components/FindingsList';
import { NotableFindings } from '../components/NotableFindings';
import {
  SchoolComparisonTable,
  type School,
  type ScoreUnit,
} from '../components/SchoolComparisonTable';
import { SchoolDrilldownTable, type SchoolTotals } from '../components/SchoolDrilldownTable';
import { SectionHeading } from '../program-evaluation.styled';
import type { PeSchoolDetail } from '../types';
import { parseFindings } from '../utils/parseFindings';
import { splitFindingText } from '../utils/splitFindingText';

export interface SchoolBySchoolProps {
  schools: School[];
  districtReclassificationRate: number | null;
  derivedDepartures: number;
  dateInferredDepartures: number;
  districtCompositeGrowthScaleScoreDelta: number | null;
  districtCompositeGrowthProficiencyLevelDelta: number | null;
  unit: ScoreUnit;
  notableFindingsText: string;
  residualCount: number;
  isSingleSchool: boolean;
  drilldown?: { schools: PeSchoolDetail[]; totals: SchoolTotals };
  threshold?: number;
}

const SMALL_N_FOOTNOTE =
  'Findings are not generated for schools with fewer than 10 EL students; small-N schools still appear in the comparison table above.';

const residualLine = (count: number): string =>
  `${count} other schools also showed notable deviation from district averages — see the comparison table above.`;

export function SchoolBySchool({
  schools,
  districtReclassificationRate,
  derivedDepartures,
  dateInferredDepartures,
  districtCompositeGrowthScaleScoreDelta,
  districtCompositeGrowthProficiencyLevelDelta,
  unit,
  notableFindingsText,
  residualCount,
  isSingleSchool,
  drilldown,
  threshold = 10,
}: SchoolBySchoolProps) {
  if (isSingleSchool || schools.length <= 1) {
    return null;
  }

  const findings: Finding[] = parseFindings(notableFindingsText).map((finding) => {
    const { headline, supporting } = splitFindingText(finding.text);
    return { text: headline, subline: supporting };
  });

  return (
    <section className="school-by-school" data-testid="school-by-school">
      <SectionHeading id="school-by-school-heading">School-by-School</SectionHeading>

      {drilldown ? (
        <SchoolDrilldownTable
          schools={drilldown.schools}
          districtTotals={drilldown.totals}
          threshold={threshold}
        />
      ) : (
        <SchoolComparisonTable
          schools={schools}
          districtReclassificationRate={districtReclassificationRate}
          districtCompositeGrowthScaleScoreDelta={districtCompositeGrowthScaleScoreDelta}
          districtCompositeGrowthProficiencyLevelDelta={
            districtCompositeGrowthProficiencyLevelDelta
          }
          unit={unit}
        />
      )}

      <DerivedDepartureNote count={derivedDepartures} color={COLORS.muted} />
      <DateInferredDepartureNote count={dateInferredDepartures} color={COLORS.muted} />

      <NotableFindings
        title="Notable Findings"
        note="Schools whose metrics diverge meaningfully from district average or own prior year."
        findings={findings}
      />

      {residualCount > 0 && (
        <p className="school-by-school-residual" data-testid="school-residual-line">
          {residualLine(residualCount)}
        </p>
      )}

      {!drilldown && (
        <p className="school-by-school-small-n-footnote" data-testid="school-small-n-footnote">
          {SMALL_N_FOOTNOTE}
        </p>
      )}
    </section>
  );
}
